import React, { useState, useRef } from 'react';
import { Email } from '../../authentication/interfaces/AuthInputFields';
import { validateInputChangeSignUpField, validateTextField } from '../../authentication/utils/validation';
import axios from '../../common/utils/interceptor';
import { getCurrentLocale } from '../../common/utils/cookies';
import ForgotPasswordLayout from './layout_01/ForgotPasswordLayout';
import PaperContainer from './PaperContainer';
import { apiUrls, routeMappings } from '../../constants';
import { getLocalizedKeyByErrorMessage } from '../../authentication/utils/errorMessage';
import { useTranslation } from 'react-i18next';
import { genericMessageHandler, a11yFocusApiErrors } from '../../common/utils/accessibilityGenericMessage';
const ForgotPasswordPage = (): React.JSX.Element => {
  const [loading, setLoading] = useState<boolean>(false);
  /** state for Forgot Password fields */
  const [forgotPasswordDetails, setForgotPasswordDetails] = useState<Email>({
    email: '',
    emailHelperText: '',
  });
  /** state for info/error alert */
  const [alert, setAlert] = useState<{ state: 'error' | 'info'; message: string }>({
    state: 'info',
    message: '',
  });
  const { t } = useTranslation();
  const errorMessageForgotPasswordRef = useRef<HTMLDivElement>(null);
  /** method for input change handler */
  const inputChangeHandler =
    (name: keyof Email) =>
    (event: React.ChangeEvent<{ value: string }>): void => {
      const value = event.target.value;
      const valueWithValidation = validateInputChangeSignUpField(name, value);

      setForgotPasswordDetails({ ...forgotPasswordDetails, ...valueWithValidation });
    };

  /** method for forgot password api */
  const forgotPassword = async (): Promise<void> => {
    setLoading(true);
    try {
      const request = {
        locale: getCurrentLocale(),
        name: forgotPasswordDetails.email,
        ...{ baseUrl: `${window.location.origin}/experience/#${routeMappings['AUTH_RESET_PASSWORD']}` },
      };
      const response = await axios.post(apiUrls.FORGOT_PASSWORD_TCPLUS, request);
      let message = '';
      if (response.data.Message) {
        if (response.data.IsError) {
          message = t(getLocalizedKeyByErrorMessage(response.data.Message));
        } else {
          message = `<span class='marginBottom10'>${t('simplifiedResetPasswordSuccessMessage1')}</span> <span>${t(
            'simplifiedResetPasswordSuccessMessage2',
          )} <a href='http://support.shl.com/' target='_blank'>${t(
            'simplifiedResetPasswordSuccessSupportLink',
          )}</a></span>`;
        }
      }
      setAlert({
        state: response.data.IsError ? 'error' : 'info',
        message: message,
      });
    } catch (e) {
      if (e && e.data && e.data.Message) {
        setAlert({
          state: 'error',
          message: t(getLocalizedKeyByErrorMessage(e.data.Message)),
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const onResetPasswordClick = async (event: React.SyntheticEvent): Promise<void> => {
    event.preventDefault();
    const emailHelperValues = validateTextField('email', forgotPasswordDetails.email);

    if (emailHelperValues.emailHelperText !== '') {
      setForgotPasswordDetails({
        ...forgotPasswordDetails,
        emailHelperText: emailHelperValues.emailHelperText,
      });
      genericMessageHandler();
      return;
    }

    await forgotPassword();
    a11yFocusApiErrors(errorMessageForgotPasswordRef);
  };

  return (
    <PaperContainer variant="small">
      <ForgotPasswordLayout
        value={forgotPasswordDetails.email}
        helperText={forgotPasswordDetails.emailHelperText}
        alert={alert}
        loading={loading}
        onResetPasswordClick={onResetPasswordClick}
        inputChangeHandler={inputChangeHandler}
        errorMessageForgotPasswordRef={errorMessageForgotPasswordRef}
      />
    </PaperContainer>
  );
};

export default ForgotPasswordPage;
